/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import React, { ReactElement, useContext } from 'react';
import RequestAccessView from '../pages/RequestAccess/RequestAccessView';
import { Pages } from './Pages';
import CoursesOverviewView from '../pages/Courses/Overview/CoursesOverviewView';
import { PrivateRoute } from './PrivateRoute';
import { observer } from 'mobx-react';
import { StoreProvider } from '../stores/StoreProvider';
import { StoreContext } from '../stores/storeContext';
import { AuthStore } from '../stores/application/AuthStore';
import StudentManagementView from '../pages/Courses/[id]/StudentManagement/StudentManagementView';
import NavBarView from '../components/Navbar/NavBarView';
import FooterView from '../components/Footer/FooterView';
import ForgotPasswordView from '../pages/ForgotPassword/ForgotPasswordView';
import ResetPasswordView from '../pages/ResetPassword/ResetPasswordView';
import ModuleEditingView from '../pages/Courses/[id]/ModuleEditing/ModuleEditingView';
import ChatView from '../pages/Courses/[id]/Chat/ChatView';
import EntriesReviewView from '../pages/Courses/[id]/EntriesReview/EntriesReviewView';
import MapView from '../pages/Courses/[id]/Map/MapView';
import PrivacyPolicyView from '../pages/LegalText/PrivacyPolicy/PrivacyPolicyView';
import ImprintView from '../pages/LegalText/Imprint/ImprintView';
import { Box, Container } from '@mui/material';
import RouterConfigStyles from './RouterConfigStyles';
import BasicNavBarView from '../components/Navbar/BasicNavBarView';
import CourseNavigationView from '../pages/Courses/[id]/CourseNavigation/CourseNavigationView';

const RouterConfig = observer((): ReactElement => {
	const storeProvider: StoreProvider = useContext(StoreContext);
	const authStore: AuthStore = storeProvider.authStore;
	const { pathname } = useLocation();
	const { classes } = RouterConfigStyles();
	const location = useLocation();

	function isNotOnLandingPage(): boolean {
		return pathname !== Pages.REQUEST_LOGIN && pathname !== Pages.RESET_PASSWORD && pathname !== Pages.FORGOT_PASSWORD;
	}

	function showCourseNav(): boolean {
		return authStore.hasAuthToken() && location.pathname.includes(`${Pages.COURSES_OVERVIEW}/`);
	}

	return (
		<Box className={classes.appWrapper}>
			<Box className={classes.container}>
				{authStore.hasAuthToken() && <NavBarView />}
				<Routes>
					<Route
						path={Pages.REQUEST_LOGIN}
						element={
							authStore.hasAuthToken() ? <Navigate to={Pages.COURSES_OVERVIEW} replace /> : <RequestAccessView />
						}
					/>
					<Route
						path={Pages.FORGOT_PASSWORD}
						element={
							authStore.hasAuthToken() ? <Navigate to={Pages.COURSES_OVERVIEW} replace /> : <ForgotPasswordView />
						}
					/>
					<Route path={Pages.RESET_PASSWORD} element={<ResetPasswordView />} />
					<Route
						path={Pages.IMPRINT}
						element={
							<>
								{!authStore.hasAuthToken() && <BasicNavBarView />}
								<Box className={classes.minHeight}>
									<ImprintView />
								</Box>
							</>
						}
					/>
					<Route
						path={Pages.PRIVACY_POLICY}
						element={
							<>
								{!authStore.hasAuthToken() && <BasicNavBarView />}
								<Box className={classes.minHeight}>
									<PrivacyPolicyView />
								</Box>
							</>
						}
					/>
					<Route
						path={Pages.COURSES_OVERVIEW}
						element={
							<Container className={classes.courseContainer}>
								<PrivateRoute component={CoursesOverviewView} />
							</Container>
						}
					/>
					<Route
						path={Pages.COURSES_OVERVIEW + '/:courseId' + Pages.MODULES}
						element={
							<Container className={classes.courseContainer}>
								{showCourseNav() && <CourseNavigationView />}
								<PrivateRoute component={ModuleEditingView} />
							</Container>
						}
					/>
					<Route
						path={Pages.COURSES_OVERVIEW + '/:courseId' + Pages.STUDENT_MANAGEMENT}
						element={
							<Container className={classes.courseContainer}>
								{showCourseNav() && <CourseNavigationView />}
								<PrivateRoute component={StudentManagementView} />
							</Container>
						}
					/>
					<Route
						path={Pages.COURSES_OVERVIEW + '/:courseId' + Pages.ENTRIES}
						element={
							<Container className={classes.courseContainer}>
								{showCourseNav() && <CourseNavigationView />}
								<PrivateRoute component={EntriesReviewView} />
							</Container>
						}
					/>
					<Route
						path={Pages.COURSES_OVERVIEW + '/:courseId' + Pages.CHAT}
						element={
							<Container className={classes.courseContainer}>
								{showCourseNav() && <CourseNavigationView />}
								<PrivateRoute component={ChatView} />
							</Container>
						}
					/>
					<Route
						path={Pages.COURSES_OVERVIEW + '/:courseId' + Pages.MAP}
						element={
							<Container className={classes.courseContainer}>
								{showCourseNav() && <CourseNavigationView />}
								<PrivateRoute component={MapView} />
							</Container>
						}
					/>
				</Routes>
			</Box>
			{isNotOnLandingPage() && <FooterView />}
		</Box>
	);
});

export default RouterConfig;
